import { EventObject } from '@xstate/store-react';
import { NextEventData } from '../../models/store/nextEvent';
import { StoreContext } from '../../models/store/context';
import { log } from '@domgen/shared/utils/log';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { getNextStepPath } from '../../routes/routes';
import { saveSession } from '../../services/session/save';
import { AppRoute } from '../../enums/appRoutes';
import { getCategoryFromApplianceCodeInCatalogue } from '../../utils/store/getCategoryFromApplianceCodeInCatalogue';
import { StoreEvent } from '../../enums/storeEvent';
import { findApplianceFromResults } from '@domgen/shared/utils/findApplianceFromResults';

/**
 * @description Next Event Handler
 * This event handler is responsible for handling the "next" event in the application.
 * When this event is triggered, it takes the form data from the event, updates the context's form with this new data, and if the current step is the MODEL_SERIAL step, it also checks if a model number is present and if there are appliance lookup results in the context. If so, it tries to find a matched appliance based on the model number and updates the form with the appliance details if a match is found. Finally, it saves the updated session context and navigates the user to the next step based on the current step key.
 * @param {StoreContext} context - The current store context
 * @param {EventObject & { data: NextEventData }} event - The event object containing the form data for the next step in event.data
 * @param {{ effect: (effect: () => Promise<void>) => void; emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void } }} enqueue - An object with an effect function to enqueue side effects and an emit function to navigate to a different route
 * @returns {StoreContext} Updated context after processing the next event and updating form data accordingly
 */
export const next = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: NextEventData },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.NEXT, data: event.data });
  const stepKey = Object.keys(event.data)[0] as StepKey;
  const form = {
    ...context.form,
    ...event.data,
  };

  // Whether the entered model was recognised in the lookup AND resolved to a catalogue
  // category. When true, appliance details are auto-filled and we skip straight to price.
  let applianceFoundInLookup = false;

  if (stepKey === StepKey.MODEL_SERIAL) {
    log.debug({ message: 'Processing MODEL_SERIAL step for next event' });
    const modelSerial = form[StepKey.MODEL_SERIAL];
    const modelNumber = modelSerial?.[FormKey.MODEL_NUMBER];

    if (modelNumber && context.applianceLookupResults) {
      log.debug({
        message: 'Model number found, attempting to find matched appliance',
        data: {
          appliances: JSON.stringify(context.applianceLookupResults),
          modelNumber,
        },
      });
      const matchedAppliance = findApplianceFromResults(
        modelNumber as string,
        context.applianceLookupResults ?? [],
      );

      if (matchedAppliance && context.app.catalogue) {
        log.debug({
          message: 'Matched appliance found, retrieving category from catalogue',
          data: {
            matchedAppliance: JSON.stringify(matchedAppliance),
          },
        });
        form[StepKey.MODEL_SERIAL] = {
          ...form[StepKey.MODEL_SERIAL],
          [FormKey.MODEL_NUMBER]: matchedAppliance.modelNumber,
        };
        const category = getCategoryFromApplianceCodeInCatalogue(
          matchedAppliance.itemTypeCode,
          context.app.catalogue,
        );

        if (category) {
          log.debug({
            message: 'Category found for matched appliance, updating form with appliance details',
            data: {
              category: JSON.stringify(category),
            },
          });
          form[StepKey.APPLIANCE_DETAILS] = {
            [FormKey.SUPER_CATEGORY]: category.categoryCode,
            [FormKey.ITEM_CATEGORY]: matchedAppliance.itemTypeCode,
          };
          applianceFoundInLookup = true;
        }
      }
    }

    // Model wasn't recognised (or its category couldn't be resolved): drop any
    // previously auto-filled appliance details so the user is sent to enter them manually.
    if (!applianceFoundInLookup) {
      delete form[StepKey.APPLIANCE_DETAILS];
    }
  }

  const updatedContext = {
    ...context,
    form,
    quote: undefined,
  };

  saveSession(updatedContext);

  if (applianceFoundInLookup) {
    // Recognised appliance: appliance details were auto-filled, so skip straight to price.
    enqueue.emit.navigate({ path: AppRoute.PURCHASE_PRICE });
  } else {
    // Unrecognised appliance (or any other step): continue to the next step in the journey.
    // For the MODEL_SERIAL step this resolves to the appliance details page.
    const nextPath = getNextStepPath(stepKey);
    if (nextPath) {
      enqueue.emit.navigate({ path: nextPath });
    }
  }

  return updatedContext;
};
